'use client'

// KawaiiButton — the one round HUD button, plus the icons that go in it.
//
// Every corner control (journal, about, gifts, camera) is this: a cream disc
// with the heavy ink outline and the solid offset shadow, so they read as a
// matching set of physical buttons rather than four different overlays. Press
// squashes the shadow, which is most of what makes a game button feel good.

import { ui, kawaii } from '../../lib/ui/theme'

export function KawaiiButton({ onClick, label, title, children, badge = false, className = '' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={title || label}
      className={`z-50 pointer-events-auto ${className}`}
      style={{
        position: 'fixed',
        width: 42,
        height: 42,
        borderRadius: '50%',
        background: kawaii.cream,
        border: kawaii.outline,
        boxShadow: kawaii.dropSm,
        color: kawaii.ink,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        transition: 'transform 90ms ease, box-shadow 90ms ease',
      }}
      onMouseDown={(e) => {
        e.currentTarget.style.transform = 'translateY(3px)'
        e.currentTarget.style.boxShadow = '0 0 0 rgba(47,38,80,0.3)'
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.boxShadow = kawaii.dropSm
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)'
        e.currentTarget.style.boxShadow = kawaii.dropSm
      }}
    >
      {children}
      {badge && (
        <span
          style={{
            position: 'absolute',
            top: -3,
            right: -3,
            width: 13,
            height: 13,
            borderRadius: '50%',
            background: '#f2748a',
            border: `2px solid ${kawaii.ink}`,
          }}
        />
      )}
    </button>
  )
}

// Shared icon geometry: chunky strokes, round joins, ink outline. Small sizes
// eat detail, so these are deliberately simple shapes.
const S = {
  fill: 'none',
  stroke: kawaii.ink,
  strokeWidth: 2.1,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
}

/** A little closed book. His diary. */
export function BookIcon({ size = 21 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 4.6h11.4a2 2 0 0 1 2 2v12.8H7a2 2 0 0 1-2-2z" fill="#b9d8f5" {...S} />
      <path d="M18.4 19.4H7a2 2 0 0 0-2 2" {...S} />
      <path d="M9.2 8.6h6M9.2 11.6h4.2" {...S} />
    </svg>
  )
}

/** A speech bubble with a question in it. "What is all this?" */
export function AskIcon({ size = 21 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M4.4 10.8c0-3.4 3.4-6.1 7.6-6.1s7.6 2.7 7.6 6.1-3.4 6.1-7.6 6.1c-.8 0-1.6-.1-2.3-.3l-3.9 2 .9-3.2a5.7 5.7 0 0 1-2.3-4.6z"
        fill="#ffd9a8"
        {...S}
      />
      <path d="M10.3 9.5a1.8 1.8 0 1 1 2.4 1.7c-.5.2-.8.6-.8 1.1v.2" {...S} />
      <circle cx="11.9" cy="14.3" r="0.9" fill={kawaii.ink} stroke="none" />
    </svg>
  )
}

/** A wrapped present. What you leave him at the shrine. */
// a tag with a hole, for the shop
export function ShopIcon({ size = 21 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3.5 12.5 12 4h8v8l-8.5 8.5a1.5 1.5 0 0 1-2.1 0l-5.9-5.9a1.5 1.5 0 0 1 0-2.1Z" />
      <circle cx="15.5" cy="8.5" r="1.4" fill="currentColor" />
      <path d="M8 13.5l3 3" />
    </svg>
  )
}

// a briefcase, for the jobs he puts up at the job centre
export function JobsIcon({ size = 21 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3.5" y="7.5" width="17" height="12" rx="2" />
      <path d="M9 7.5V5.8A1.3 1.3 0 0 1 10.3 4.5h3.4A1.3 1.3 0 0 1 15 5.8v1.7" />
      <path d="M3.5 12.5h17" />
    </svg>
  )
}

export function GiftIcon({ size = 21 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4.2" y="10.2" width="15.6" height="9.4" rx="1.8" fill="#f2b6c6" {...S} />
      <rect x="3.1" y="6.9" width="17.8" height="3.6" rx="1.4" fill="#ffd9a8" {...S} />
      <path d="M12 6.9v12.7" {...S} />
      <path d="M12 6.9c-1.9 0-3.6-.6-3.6-2s1.1-1.9 2-1.5c1.1.5 1.6 2 1.6 3.5zM12 6.9c1.9 0 3.6-.6 3.6-2s-1.1-1.9-2-1.5c-1.1.5-1.6 2-1.6 3.5z" fill="#ffd9a8" {...S} />
    </svg>
  )
}

/** A camera. Photo mode. */
export function CameraIcon({ size = 21 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3.4 9.2a1.8 1.8 0 0 1 1.8-1.8h2.1l1.1-1.8a1 1 0 0 1 .9-.5h5.4a1 1 0 0 1 .9.5l1.1 1.8h2.1a1.8 1.8 0 0 1 1.8 1.8v8.4a1.8 1.8 0 0 1-1.8 1.8H5.2a1.8 1.8 0 0 1-1.8-1.8z" fill="#bfe3c6" {...S} />
      <circle cx="12" cy="13.2" r="3.3" fill={kawaii.cream} {...S} />
    </svg>
  )
}


/**
 * KawaiiCta — the pressable text button.
 *
 * Every panel had been rolling its own, which is how the shrine ended up with
 * a pale-yellow-on-cream sign-in that measured about 1.5:1 and was effectively
 * invisible. One component, three intents, all checked against the cream:
 *   primary  amber fill, ink label. The thing you're meant to press.
 *   solid    ink fill, cream label. For a confirm that should feel heavier.
 *   quiet    outline only. For secondary actions.
 * Pressing squashes the shadow, same as the round buttons.
 */
export function KawaiiCta({
  children,
  onClick,
  intent = 'primary',
  type = 'button',
  disabled = false,
  small = false,
  className = '',
  style = {},
  ...rest
}) {
  const fills = {
    primary: { background: '#f0b064', color: kawaii.ink },
    solid: { background: kawaii.ink, color: '#fdf6ea' },
    quiet: { background: 'transparent', color: kawaii.ink },
  }
  const press = (v) => (e) => {
    if (disabled) return
    e.currentTarget.style.transform = v ? 'translateY(3px)' : 'translateY(0)'
    e.currentTarget.style.boxShadow = v ? '0 0 0 rgba(47,38,80,0.3)' : '0 3px 0 rgba(47,38,80,0.3)'
  }
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={className}
      style={{
        ...fills[intent],
        border: kawaii.outline,
        borderRadius: kawaii.radiusPill,
        boxShadow: '0 3px 0 rgba(47,38,80,0.3)',
        fontFamily: ui.font,
        fontWeight: 800,
        fontSize: small ? 12 : 14,
        padding: small ? '6px 14px' : '10px 22px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        transition: 'transform 90ms ease, box-shadow 90ms ease',
        ...style,
      }}
      onMouseDown={press(true)}
      onMouseUp={press(false)}
      onMouseLeave={press(false)}
      {...rest}
    >
      {children}
    </button>
  )
}

/** Shared panel chrome for the popups, so journal/about/gifts all match. */
export function kawaiiPanel() {
  return {
    background: kawaii.cream,
    border: kawaii.outline,
    borderRadius: kawaii.radius,
    boxShadow: kawaii.drop,
    color: kawaii.ink,
    fontFamily: ui.font,
  }
}

/** The round close button used in every popup header. */
export function CloseButton({ onClick, tabIndex }) {
  return (
    <button
      type="button"
      onClick={onClick}
      tabIndex={tabIndex}
      aria-label="close"
      style={{
        width: 26,
        height: 26,
        borderRadius: '50%',
        background: kawaii.creamDeep,
        border: kawaii.outlineThin,
        color: kawaii.ink,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
        flexShrink: 0,
      }}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M6 6l12 12M18 6L6 18" stroke={kawaii.ink} strokeWidth="3" strokeLinecap="round" />
      </svg>
    </button>
  )
}

export default KawaiiButton
